import { Button, FormGroup, Label } from "reactstrap";
import PageLayout from "../layout/page";
import { arrowLeft, arrowRight } from "../../constant/icons";
import ReactDatePicker from "react-datepicker";
import { DataView } from "../../components/table/table";
import { columns } from "./columns";
import { useNavigate } from "react-router-dom";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { getDate } from "../../helper/utility";
import * as Yup from "yup";
import { get } from "../../helper/api";
import { useState } from "react";
import { message } from "../../components/toast";

const formatDateToDDMMYYYY = (date: Date | null): string => {
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

// Fetch balance sheet data from the API
const fetchBalanceSheetData = async (startDate: Date, endDate: Date, tenantId: string): Promise<any> => {
  const fromDate = formatDateToDDMMYYYY(startDate);
  const toDate = formatDateToDDMMYYYY(endDate);
  const urlWithParams = `/balanceSheet?tenantId=${tenantId}&from_date=${fromDate}&to_date=${toDate}`;

  try {
    const response = await get(urlWithParams);
    console.log("API Response:", response);
    if (response.status && response.data) { return response.data; }
    return [];
  } catch (error) {
    console.error("API call failed:", error);
    return [];
  }
};

// Process Excel data and match with API data, optionally merging records by accountName
const processExcelData = (props: any, apiData: any[]): any[] => {
  const mergeData = props.mergeData ?? false; // Default to false if mergeData is not provided
  const mergedRecordsMap = new Map<string, any>(); // Used when mergeData is true
  const matchedRecords: any[] = []; // Used when mergeData is false

  if (props.uploadFile && props.selectedSheet && props.selectedSheet.data && props.selectedSheet.data.rowData) {
    const sheet = props.selectedSheet.data;
    const headers = sheet.sourceColumnList?.[0] || [];
    const rows = sheet.rowData;
    const mappings = props.mappings || {};
    console.log({ headers });
    const accountNameColumn = mappings.accountName;
    const accountCodeColumn = mappings.accountCode;
    const creditAmountColumn = mappings.creditAmount;
    const debitAmountColumn = mappings.debitAmount;
    const amountColumn = mappings.amount;
    const balanceColumn = mappings.amount;

    const accountNameIndex = headers.indexOf(accountNameColumn);
    const accountCodeIndex = headers.indexOf(accountCodeColumn);
    const creditAmountIndex = headers.indexOf(creditAmountColumn);
    const debitAmountIndex = headers.indexOf(debitAmountColumn);
    const amountIndex = headers.indexOf(amountColumn);
    const balanceIndex = headers.indexOf(balanceColumn);

    rows.forEach((row: any, index: any) => {
      if (!row || row.length === 0) return;

      const accountName = accountNameIndex !== -1 ? String(row[accountNameIndex] || "") : "";
      const accountCode = accountCodeIndex !== -1 ? String(row[accountCodeIndex] || "") : "";
      const creditUser = creditAmountIndex !== -1 && row[creditAmountIndex] != null ? Number(row[creditAmountIndex]) || 0 : 0;
      const debitUser = debitAmountIndex !== -1 && row[debitAmountIndex] != null ? Number(row[debitAmountIndex]) || 0 : 0;
      const debitUserDiff = amountIndex !== -1 && row[amountIndex] != null ? Number(row[amountIndex]) || 0 : 0;
      const balanceDiff = balanceIndex !== -1 && row[balanceIndex] != null ? Number(row[balanceIndex]) || 0 : 0;
      // const cbDifferenceAmount = 
      // Find matching API record
      const apiMatch = apiData.find((apiRow: any) => {
        if (!apiRow.account_id || apiRow.account_id.length < 2) return false;
        // const [apiAccountCode, ...apiAccountNameParts] = apiRow.account_id[1].split(" ");
        // const apiAccountName = apiAccountNameParts.join(" ");
        return /* accountCode === apiAccountCode && */ accountName === apiRow.account_name;
      });

      if (apiMatch) {
        const rowData = {
          account: accountCode,
          accountName: accountName,
          accountCreditUser: creditUser,
          accountDebitUser: debitUser,
          accountDebitUserDiff: (balanceDiff - apiMatch.balance),
          balance: balanceDiff,
          accountBalance: apiMatch.balance || 0,
          accountCreditApi: apiMatch.credit || 0,
          accountDebitApi: apiMatch.debit || 0,
          balanceApi: apiMatch.balance || 0,
        };

        if (mergeData) {
          // Merge records with the same accountName
          if (mergedRecordsMap.has(accountName)) {
            const existingRecord = mergedRecordsMap.get(accountName);
            // Check for differing account codes
            if (existingRecord.accountName !== rowData.accountName) {
              console.warn(
                `Different account codes for Account Name "${accountName}": ${existingRecord.account} and ${rowData.account}. Using the first code: ${existingRecord.account}.`
              );
            }
            // Sum the numeric fields
            existingRecord.accountCreditUser += rowData.accountCreditUser;
            existingRecord.accountDebitUser += rowData.accountDebitUser;
            existingRecord.accountDebitUserDiff += rowData.accountDebitUserDiff;
            existingRecord.accountBalance += rowData.balanceApi;
            existingRecord.balanceApi += rowData.balanceApi;
          } else {
            mergedRecordsMap.set(accountName, rowData);
          }
        } else {
          // Do not merge, add each matched record as is
          matchedRecords.push(rowData);
        }
      }
    });
  }

  // Convert to array with tableId and id
  if (mergeData) {
    return Array.from(mergedRecordsMap.values()).map((record: any, index: number) => ({
      ...record,
      tableId: index + 1,
      id: index + 1,
    }));
  } else {
    if (!Array.isArray(matchedRecords) || matchedRecords.length === 0) {
      message("error", "No Matching Records are found ");
      return [];
    }

    return matchedRecords.map((record: any, index: number) => ({
      ...record,
      tableId: index + 1,
      id: index + 1,
    }));
  }
};

const BalanceSheetView = (props: any) => {
  const navigate = useNavigate();
  const [matchedData, setMatchedData] = useState<any[]>([]);
  const validationSchema = Yup.object({
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date().required("End date is required"),
  });

  // useEffect(() => {
  //   const fetchAndProcessData = async () => {
  //     if (!startDate || !endDate) return;
  //     //this is the api data we are fatching and later we comapare it with the excel sheet data than shaw in the dataview component
  //     const apiData = await fetchBalanceSheetData(startDate, endDate, props.tenantId);
  //     const processedData = processExcelData(props, apiData);

  //     setMatchedData(processedData);
  //   };

  //   fetchAndProcessData();
  // }, [startDate, endDate, props]);

  const handleSubmit = async (values: any) => {
    const apiData = await fetchBalanceSheetData(values?.startDate, values?.endDate, props.tenantId);
    console.log({ props });
    const processedData = processExcelData(props, apiData);
    setMatchedData(processedData);
  };

  return (
    <PageLayout
      title={<Button color="link" className="btn-back" onClick={() => navigate("/balanceSheet")}>{arrowLeft.icon}</Button>}
      View={
        <>
          <Formik
            initialValues={{ startDate: new Date(), endDate: new Date() }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, values }) => (
              <Form>
                <div className="balancesheet-view">
                  <div className="balancesheet-view-head p-3 pb-0 mb-3">
                    <FormGroup className="col-3 form-floating sticky_label ps-0">
                      <Field
                        name="startDate"
                        component={ReactDatePicker}
                        placeholderText={"DD/MM/YYYY"}
                        dateFormat={"dd/MM/yyyy"}
                        className="form-control w-100"
                        popperPlacement="auto"
                        selected={getDate(values?.startDate)}
                        onChange={(date: any) => { setFieldValue("startDate", date); }}
                      />
                      <ErrorMessage name="startDate" component="div" className="text-danger" />
                      <Label>conversion Start date <span style={{ color: "red" }}>*</span></Label>
                    </FormGroup>
                    <FormGroup className="col-3 form-floating sticky_label ps-0">
                      <Field
                        name="endDate"
                        component={ReactDatePicker}
                        placeholderText={"DD/MM/YYYY"}
                        dateFormat={"dd/MM/yyyy"}
                        className="form-control w-100"
                        popperPlacement="auto"
                        selected={getDate(values?.endDate)}
                        onChange={(date: any) => { setFieldValue("endDate", date); }}
                      />
                      <ErrorMessage name="endDate" component="div" className="text-danger" />
                      <Label>conversion End date <span style={{ color: "red" }}>*</span></Label>
                    </FormGroup>
                    <div className="btn-box mb-3"><button className="btn btn-primary" title="submit" type="submit">Submit</button></div>
                  </div>
                  {matchedData?.length > 0 &&
                    <DataView columns={columns({})} rows={matchedData} />}
                </div>
              </Form>
            )}
          </Formik>
        </>
      }
    />
  );
};

export { BalanceSheetView };